import {createSlice} from '@reduxjs/toolkit';
import {
  AqForecastResponse,
  HistoryMeasureResult,
  WeatherForecast,
  WeatherWarningListResponse,
} from '../../api/types';
import {NetatmoCommon} from '../../utils';

type Props = {
  historyData: Record<string, Record<string, number | null>>;
  forecastData: Record<string, WeatherForecast>;
  stationWeatherWarnings?: Record<string, WeatherWarningListResponse>;
  locationForecastData?: Record<string, WeatherForecast>;
  locationWeatherWarnings?: Record<string, WeatherWarningListResponse>;
  aqForecastData: Record<string, AqForecastResponse>;
  locationAqForecastData: Record<string, AqForecastResponse>;
};

export const dataSlice = createSlice({
  name: 'weatherData',
  initialState: {
    historyData: {},
    forecastData: {},
    stationWeatherWarnings: {},
    locationForecastData: {},
    locationWeatherWarnings: {},
    aqForecastData: {},
    locationAqForecastData: {},
  } as Props,
  reducers: {
    pushWeatherHistorySeries: (
      state,
      {payload: data}: {payload: HistoryMeasureResult[]},
    ) => {
      const handler = new NetatmoCommon.ChartsDataContainer(state.historyData);
      Object.values(data).forEach((item) => {
        handler.update(item);
      });
      state.historyData = handler.getData();
    },
    pushStationForecast: (
      state,
      {
        payload: {stationId, forecastModel, meshSize, data},
      }: {
        payload: {
          stationId: number;
          forecastModel: string;
          meshSize?: string;
          data: WeatherForecast;
        };
      },
    ) => {
      const newStationForecastData = {} as Record<string, WeatherForecast>;
      const key = `${stationId}-${forecastModel}-${meshSize}`;
      newStationForecastData[key] = data;
      state.forecastData = {...state.forecastData, ...newStationForecastData};
    },
    pushStationWeatherWarnings: (
      state,
      {
        payload: {stationId, warningListResponse},
      }: {
        payload: {
          stationId: number;
          warningListResponse: WeatherWarningListResponse;
        };
      },
    ) => {
      const newStationWeatherWarnings = {} as Record<
        string,
        WeatherWarningListResponse
      >;
      const key = `${stationId}`;
      newStationWeatherWarnings[key] = warningListResponse;
      state.stationWeatherWarnings = {
        ...state.stationWeatherWarnings,
        ...newStationWeatherWarnings,
      };
    },
    pushLocationForecast: (
      state,
      {
        payload: {locationHash, forecastModel, meshSize, data},
      }: {
        payload: {
          locationHash: string;
          forecastModel: string;
          meshSize: string | null;
          data: WeatherForecast;
        };
      },
    ) => {
      const newLocationForecastData = {} as Record<string, WeatherForecast>;
      const key = `${locationHash}-${forecastModel}-${meshSize}`;
      newLocationForecastData[key] = data;
      state.locationForecastData = {
        ...state.locationForecastData,
        ...newLocationForecastData,
      };
    },
    pushLocationWeatherWarnings: (
      state,
      {
        payload: {locationHash, warningListResponse},
      }: {
        payload: {
          locationHash: string;
          warningListResponse: WeatherWarningListResponse;
        };
      },
    ) => {
      const newLocationWeatherWarnings = {} as Record<
        string,
        WeatherWarningListResponse
      >;
      const key = `${locationHash}`;
      newLocationWeatherWarnings[key] = warningListResponse;
      state.locationWeatherWarnings = {
        ...state.locationWeatherWarnings,
        ...newLocationWeatherWarnings,
      };
    },
    pushStationAqForecast: (
      state,
      {
        payload: {stationId, data},
      }: {
        payload: {stationId: number; data: AqForecastResponse};
      },
    ) => {
      state.aqForecastData = {
        ...state.aqForecastData,
        [String(stationId)]: data,
      };
    },
    pushLocationAqForecast: (
      state,
      {
        payload: {locationHash, data},
      }: {
        payload: {locationHash: string; data: AqForecastResponse};
      },
    ) => {
      state.locationAqForecastData = {
        ...state.locationAqForecastData,
        [locationHash]: data,
      };
    },
  },
});

export const {
  pushWeatherHistorySeries,
  pushStationForecast,
  pushStationWeatherWarnings,
  pushLocationForecast,
  pushLocationWeatherWarnings,
  pushStationAqForecast,
  pushLocationAqForecast,
} = dataSlice.actions;

export default dataSlice.reducer;
